import React, {
  Fragment,
  useEffect,
  useRef
} from "react";
import { Mui, useDarkMode } from "@osu/react-ui";
import LightbulbIcon from "../../../Common/components/LightbulbIcon";
import ValidatedURL from "../../../util/url-validation-utility";
import ExternalLink from "../../../Common/components/ExternalLink";
import useHTMLProcessor from "../../hooks/useHTMLProcessor";

const useStyles = Mui.makeStyles((theme) => ({
  help: {
    border: "solid 0.15rem",
    borderColor: theme.palette.primary.main,
    borderRadius: "0.5rem",
    marginBottom: "1rem",
    [theme.breakpoints.down(470)]: {
      flexWrap: "wrap",
      justifyContent: "center",
    },
  },
  helpIcon: {
    alignSelf: "center",
    display: "flex",
    paddingRight: "1rem",
  },
}));

function HelpText({ subtopic, topic, helpText, uuid: _uuid }) {
  const { darkMode } = useDarkMode();
  const hashForMode = darkMode ? "#DARK" : "#LIGHT";
  const uuid = _uuid ? _uuid + hashForMode : "";
  const externalLinkRef = useRef();
  const classes = useStyles();
  let heading = `Common Topics for `;
  if (subtopic) {
    heading += `${subtopic}`;
  } else {
    heading += `${topic}`;
  }
  const cachedHTML = useRef({});
  const { _sanitizedHTML, modifiedHTML } = useHTMLProcessor({
    externalLinkRef,
    uuid,
    html: helpText,
    cache: cachedHTML?.current,
  });
  const sanitizedHTMLForUUID = uuid && modifiedHTML[uuid];

  useEffect(() => {
    if (uuid) {
      const existing = cachedHTML?.current?.[uuid];
      if (!existing || existing !== sanitizedHTMLForUUID) {
        cachedHTML.current[uuid] = sanitizedHTMLForUUID;
      }
    }
  }, [uuid, sanitizedHTMLForUUID]);
  const helpTextHTML = sanitizedHTMLForUUID || _sanitizedHTML
  return (
    <Fragment>
      <div
        ref={externalLinkRef}
        aria-hidden
        data-dev="used-for-html-parser"
        className="display-none"
      >
        {Object.values(ValidatedURL.options).map((type, i) => (
          <ExternalLink
            key={[uuid, i, type].join("#")}
            data-type={type}
            type={type}
          />
        ))}
      </div>
      {helpTextHTML && (
        <Mui.Box
          display="flex"
          justifyContent="flex-start"
          padding={2}
          className={classes.help}
        >
          <Mui.Box className={classes.helpIcon}>
            <LightbulbIcon color="primary" />
          </Mui.Box>
          <Mui.Box component="section">
            <Mui.Typography component="h3" color="secondary">
              {heading}
            </Mui.Typography>
            <Mui.Typography
              component="div"
              className={classes.body}
              variant="body1"
              dangerouslySetInnerHTML={{ __html: helpTextHTML }}
            />
          </Mui.Box>
        </Mui.Box>
      )}
    </Fragment>
  );
}

export default HelpText;
